import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  Layers,
  Tag,
  CircleCheck,
  AlertTriangle,
  Wind,
  Archive,
  Sparkles,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Flux direct et sécurisé ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Flux direct
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Vos cartons de déménagement livrés à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Nous avons réinventé le mode d&apos;approvisionnement pour les
            résidents et professionnels suresnois. Finis les allers-retours
            fastidieux et la perte de temps logistique&nbsp;: nous
            introduisons un système de livraison à l&apos;efficacité
            chirurgicale.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Maîtrise totale de la chaîne de transport",
              body: "Votre commande ne transite par aucun centre de tri tiers. C'est notre propre flotte d'utilitaires franciliens qui charge vos fournitures depuis nos entrepôts pour les déposer directement sur votre site. Vous bénéficiez d'une traçabilité absolue et d'un soin maximal.",
              Icon: Truck,
            },
            {
              title: "Garantie structurelle d'usine",
              body: "Nos véhicules à parois rigides isolent parfaitement le matériel contre l'air humide ambiant et les poussières. Vos structures de carton arrivent chez vous avec un taux d'hygrométrie parfait, préservant 100% de leur raideur et de leur force de fermeture mécanique.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Blindage interne : 3 accessoires de protection ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFE9D6] border border-[#FF7A00]/30 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} />
            Blindage interne
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Notre arsenal de protection et d&apos;enveloppement
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce qu&apos;un contenant extérieur robuste doit toujours
            s&apos;accompagner d&apos;une isolation interne efficace, nous
            mettons à votre disposition une gamme complète de fournitures
            techniques pour neutraliser les impacts et les frottements.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              title: "Le film à bulles d'air alvéolé",
              body: "Indispensable pour créer une barrière amortissante continue autour de vos pièces fragiles, de la vaisselle aux équipements électroniques.",
              Icon: Layers,
              to: "/produit/papier-bulle-10m",
              label: "Voir le papier bulle",
            },
            {
              title: "Le papier kraft de calage",
              body: "La solution idéale pour combler les espaces vides à l'intérieur de vos cartons. En éliminant l'air libre, vous empêchez les objets de se déplacer et renforcez la résistance globale du bloc face à la pression.",
              Icon: Tag,
              to: "/produit/papier-blanc-journal",
              label: "Voir le papier de calage",
            },
            {
              title: "Les gaines et protections d'angles",
              body: "Des modules en mousse haute densité conçus pour épouser les arêtes de vos cadres, miroirs ou écrans, évitant les éclats lors des passages de portes étroits.",
              Icon: ShieldCheck,
              to: "/emballage-protection",
              label: "Voir les protections",
            },
          ].map(({ title, body, Icon, to, label }) => (
            <Link
              key={title}
              to={to}
              className="block bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] hover:border-[#237cb5] hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-16px_rgba(35,124,181,0.35)] transition-all duration-300 p-5 group"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[14.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[13.5px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#237cb5] group-hover:gap-2.5 transition-all">
                {label}
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-suresnes-cta-emballage"
          >
            Voir tout l&apos;emballage et la protection
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Guide expert : préparer les meubles pour stockage long ======= */}
    <section className="py-14 md:py-20 bg-[#0f1d2b] text-white relative overflow-hidden">
      <div
        aria-hidden="true"
        className="absolute -top-32 -right-24 h-72 w-72 rounded-full bg-[#FF7A00]/20 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-[#237cb5]/30 blur-3xl"
      />
      <div className="container-cdm relative">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] uppercase tracking-[0.18em] font-bold text-[#FF7A00] mb-4">
            <Archive className="h-3.5 w-3.5" strokeWidth={2.6} />
            Guide expert
          </div>
          <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-white">
            Comment préparer les meubles pour un stockage long en garde-meuble&nbsp;?
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-white/75 leading-relaxed">
            Le stockage en box impose des contraintes spécifiques liées au
            temps et à la stagnation de l&apos;air. Pour retrouver vos
            meubles dans un état impeccable après plusieurs mois ou années
            de garde-meuble, suivez ces règles professionnelles.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
          {/* Card 1 — Warning (critical mistake) */}
          <div className="relative bg-white/[0.04] rounded-2xl border border-[#FF7A00]/40 p-6 md:p-7">
            <div className="flex items-center gap-2.5">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#FF7A00] text-white shrink-0">
                <AlertTriangle className="h-5 w-5" strokeWidth={2.6} />
              </span>
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#FF7A00]">
                Erreur n°1 à éviter
              </div>
            </div>
            <h3 className="mt-4 font-display font-extrabold text-[17px] leading-tight text-white">
              Bannissez le film étirable plastique directement sur le bois ou le cuir
            </h3>
            <p className="mt-3 text-[14px] text-white/75 leading-relaxed">
              C&apos;est l&apos;erreur la plus fréquente. Le plastique
              hermétique emprisonne l&apos;humidité résiduelle et provoque
              de la condensation, ce qui génère des moisissures ou fait
              travailler le bois. Utilisez systématiquement des couvertures
              de déménagement en tissu ou du feutre respirant pour la
              première couche.
            </p>
            <div className="mt-5">
              <Link
                to="/produit/couverture-demenagement"
                className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#FF7A00] hover:gap-2.5 transition-all"
                data-testid="city-suresnes-cta-couverture"
              >
                Voir les couvertures de déménagement
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
              </Link>
            </div>
          </div>

          {/* Card 2 — Best practice */}
          <div className="relative bg-white/[0.04] rounded-2xl border border-white/15 p-6 md:p-7">
            <div className="flex items-center gap-2.5">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white shrink-0">
                <ShieldCheck className="h-5 w-5" strokeWidth={2.6} />
              </span>
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#237cb5]">
                Bonne pratique
              </div>
            </div>
            <h3 className="mt-4 font-display font-extrabold text-[17px] leading-tight text-white">
              Utilisez le plastique uniquement en bouclier extérieur
            </h3>
            <p className="mt-3 text-[14px] text-white/75 leading-relaxed">
              Vous pouvez appliquer du film étirable par-dessus vos
              couvertures en tissu uniquement pour les maintenir en place
              et créer une barrière étanche contre la poussière extérieure
              et les insectes.
            </p>
            <div className="mt-5">
              <Link
                to="/produit/film-etirable"
                className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#237cb5] hover:gap-2.5 transition-all"
                data-testid="city-suresnes-cta-film-etirable"
              >
                Voir le film étirable
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
              </Link>
            </div>
          </div>

          {/* Card 3 — Ventilation */}
          <div className="relative bg-white/[0.04] rounded-2xl border border-white/15 p-6 md:p-7">
            <div className="flex items-center gap-2.5">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white shrink-0">
                <Wind className="h-5 w-5" strokeWidth={2.6} />
              </span>
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#237cb5]">
                Ventilation
              </div>
            </div>
            <h3 className="mt-4 font-display font-extrabold text-[17px] leading-tight text-white">
              Surélevez et aérez la base
            </h3>
            <p className="mt-3 text-[14px] text-white/75 leading-relaxed">
              Ne posez jamais vos matelas, canapés ou meubles en bois brut
              directement sur la dalle en béton du box de stockage. Placez
              une bâche de sol ou des palettes en bois, puis disposez vos
              meubles. Pensez également à laisser les portes des armoires
              et les tiroirs de vos commodes très légèrement entrouverts
              pour permettre à l&apos;air de circuler à l&apos;intérieur.
            </p>
            <div className="mt-5">
              <Link
                to="/produit/housse-matelas-2-places"
                className="inline-flex items-center gap-1.5 text-[13px] font-bold text-[#237cb5] hover:gap-2.5 transition-all"
                data-testid="city-suresnes-cta-housse-matelas"
              >
                Voir les housses matelas
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.6} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-suresnes-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
